import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import _ from 'underscore';
import { withRouter, useLocation, useHistory } from 'react-router-dom';
import LeaderboardDisplay from './LeaderboardDisplay.js';
import { initNavbarState, setNavbarTitle } from 'actions/navbar.js';
import { fetchLeaderboard } from 'actions/leaderboards.js';
import { getName, getCompany, formatLeaderboardValue, dailyGoal, dailyMetric } from 'modules/leaderboards/tools.js';

const shareBlockedStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  textAlign: 'center',
  fontSize: '1.25rem',
};

const LeaderboardContainer = ({ dispatch, leaderboardState, match, fetchLeaderboard, userProfileId, teamId, history }) => {
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState();
  const [column, setColumn] = useState();
  const [leaderboard, setLeaderboard] = useState();
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [columns, setColumns] = useState([[]]);

  const location = useLocation();
  const [refreshInterval] = useState(
    parseInt(
      location.search
        .replace('?', '')
        .split('&')
        .find((v) => v?.startsWith('refreshInterval='))
        ?.split('=')?.[1] ?? 0
    ) || 60 * 1000
  );
  const [code] = useState(
    location.search
      .replace('?', '')
      .split('&')
      .find((v) => v?.startsWith('code='))
      ?.split('=')?.[1]
  );

  useEffect(() => {
    setBusy(true);
    dispatch(initNavbarState({ enable: true, menuEnable: false, backButtonEnable: true, title: '' }));
    dispatch({ type: 'SET_SIDEBAR_ENABLE', enable: false });
    loadLeaderboard();
    return () => {
      leaderboardState.recordsMap = {};
    };
  }, []);

  useEffect(() => {
    const refresh = setInterval(() => loadLeaderboard(), refreshInterval);
    return () => clearInterval(refresh);
  }, [refreshInterval, code]);

  const loadLeaderboard = () => {
    const leaderboardId = match.params.id;
    const routeTeamId = match.params.teamId;
    return fetchLeaderboard(leaderboardId, true, code)
      .then((result) => {
        const { leaderboard: lb, records: { rows, total: recordTotal } } = result;
        if (!lb?.shareable) {
          setLoadError('Leaderboard not found');
          setLeaderboard(null);
          return;
        }
        setLoadError(null);
        lb.teamId = routeTeamId;
        if (routeTeamId) {
          lb.scope = 'userProfile';
          lb.type = 'individual';
        }
        const col = lb ? lb.column || 'points' : null;
        setColumn(col);
        setLeaderboard({ ...lb });
        dispatch(
          initNavbarState({
            enable: true,
            menuEnable: false,
            backButtonEnable: true,
            title: lb.teamInfo ? lb.teamInfo.title : lb.title,
          })
        );
        setTotal(recordTotal);
        setRecords(rows ?? []);
        setColumns(_.chunk(rows, Math.ceil(rows.length / 4) || 1));
      })
      .catch((err) => {
        setLoadError('Leaderboard not found');
        setLeaderboard(null);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const isMember = (record) => {
    if (userProfileId == null) return false;
    if (record.userProfiles) {
      return record.userProfiles.some((p) => p.id === userProfileId);
    }
    if (record.teamId) return record.teamId === teamId;
    return userProfileId === record.userProfileId;
  };

  const onClickChange = (record) => {
    if (record?.teamId) {
      history.push(`${location.pathname}/${record.teamId}`);
    }
  };

  const isClickable = (record) => Boolean(record?.team);

  let targetPoints = 0;
  if (leaderboard && records?.length && records[0][column] > targetPoints) {
    targetPoints = records[0][column];
  }

  if (loadError) {
    return <div style={shareBlockedStyle}>{loadError}</div>;
  }

  return (
    <LeaderboardDisplay
      getName={getName}
      getCompany={getCompany}
      formatLeaderboardValue={formatLeaderboardValue}
      dailyMetric={dailyMetric}
      dailyGoal={dailyGoal}
      leaderboard={leaderboard}
      targetPoints={targetPoints}
      column={column}
      isMember={isMember}
      isClickable={isClickable}
      busy={busy}
      total={total}
      onClickChange={onClickChange}
      columns={columns}
    />
  );
};

const mapStateToProps = (state) => ({ leaderboardState: state.leaderboards });

const mapDispatchToProps = (dispatch) => ({
  fetchLeaderboard: (id, force, code) => dispatch(fetchLeaderboard(id, force, code)),
  dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(LeaderboardContainer);
